/**
 * The handbook's two loads: the index, and one guide.
 *
 * Search is debounced rather than fired per keystroke - the server consults the
 * bodies for a query, and that is the one read in this section worth not doing
 * four times while somebody types "wordpress".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../lib/api";
import { getGuide, listGuides, type KbArticle, type KbIndex } from "../../../lib/kb";

const SEARCH_DEBOUNCE_MS = 220;

export function useGuideIndex(opts: { includeDrafts: boolean }) {
  const [data, setData] = useState<KbIndex | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string | null>(null);

  const load = useCallback(
    async (query: string, cat: string | null) => {
      setLoading(true);
      setError(false);
      try {
        setData(await listGuides({ q: query || undefined, category: cat ?? undefined, includeDrafts: opts.includeDrafts }));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [opts.includeDrafts],
  );

  useEffect(() => {
    const id = setTimeout(() => void load(q, category), q ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(id);
  }, [q, category, load]);

  return { data, loading, error, q, setQ, category, setCategory, reload: () => void load(q, category) };
}

export function useGuide(slug: string | undefined) {
  const [article, setArticle] = useState<KbArticle | null>(null);
  const [loading, setLoading] = useState(true);
  /** 404 only. A failed request is a different sentence - see `error`. */
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState(false);
  // Which slug the state belongs to, so a slow answer for the previous guide
  // cannot paint over the one the reader is on now.
  const wanted = useRef<string | undefined>(slug);

  useEffect(() => {
    wanted.current = slug;
    if (!slug) return;
    setLoading(true);
    setMissing(false);
    setError(false);
    getGuide(slug)
      .then((a) => {
        if (wanted.current === slug) setArticle(a);
      })
      .catch((e) => {
        if (wanted.current !== slug) return;
        // "There is no such guide" and "the request failed" send a reader to two
        // different places. Telling somebody a link is dead when the server was
        // simply unreachable is how a working link gets deleted from a task.
        if (e instanceof ApiError && e.status === 404) setMissing(true);
        else setError(true);
      })
      .finally(() => {
        if (wanted.current === slug) setLoading(false);
      });
  }, [slug]);

  return { article, loading, missing, error, setArticle };
}
